import { Dispatch, SetStateAction, useCallback } from "react";
import { hfbXxxStringTranslator, useTranslation } from "@coworker/locales";
import { departments } from "../../InputPopup/UserFiltersRow/FilterChoiceData";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { areaIsWholeStore } from "../../InputPopup/UserFiltersRow/filterChoices";
import { WHOLE_STORE_HFB } from "../constants";

interface ListProps {
  filter: string | undefined;
  onSelect: (value: string) => void;
  isAcesAndKing: boolean;
  area: string;
  setArea: Dispatch<SetStateAction<string>>;
  multiSelect?: boolean;
  selectedAreas?: string[];
  onMultiSelect?: (values: string[]) => void;
  maxSelections?: number;
}

export function HFBList({
  filter,
  onSelect,
  isAcesAndKing,
  area,
  setArea,
  multiSelect = false,
  selectedAreas = [],
  onMultiSelect,
  maxSelections = 3,
}: ListProps) {
  const { t } = useTranslation();

  const displayTextIncludesFilter = useCallback(
    ({ hfbNumber }: { hfbNumber: string }) => {
      if (!filter) return true;
      const displayText = `HFB ${hfbNumber} - ${hfbXxxStringTranslator(hfbNumber, t)}`;
      return displayText.toLowerCase().includes(filter.toLowerCase());
    },
    [filter, t]
  );
  const handleSelectedAreaLogic = (
    isCurrentlySelected: boolean,
    hfbType: string
  ) => {
    if (isCurrentlySelected) {
      return selectedAreas.filter((item) => item !== hfbType);
    }
    if (areaIsWholeStore(hfbType)) {
      return [hfbType];
    }
    return [
      ...selectedAreas.filter((item) => !areaIsWholeStore(item)),
      hfbType,
    ];
  };
  const handleCheckboxChange = (hfbType: string) => {
    if (!onMultiSelect) return;

    const newSelection: string[] = handleSelectedAreaLogic(
      selectedAreas.includes(hfbType),
      hfbType
    );

    onMultiSelect(newSelection);
  };

  return (
    <FixaListView id="hfb-list">
      {multiSelect && (
        <FixaListViewItem
          title={t("maxHfbSelectionInfoString", { max: maxSelections })}
          inset
          emphasised
          disabled
        />
      )}
      {isAcesAndKing &&
        displayTextIncludesFilter({ hfbNumber: "whole=whole" }) && (
          <FixaListViewItem
            title={t("wholeStoreString")}
            control={multiSelect ? "checkbox" : "radiobutton"}
            inset
            onChange={() => {
              if (multiSelect) {
                handleCheckboxChange(WHOLE_STORE_HFB);
              } else {
                setArea(WHOLE_STORE_HFB);
                onSelect(WHOLE_STORE_HFB);
              }
            }}
            controlProps={{
              checked: multiSelect
                ? selectedAreas.includes(WHOLE_STORE_HFB)
                : area === WHOLE_STORE_HFB,
              value: WHOLE_STORE_HFB,
            }}
          />
        )}
      {departments.filter(displayTextIncludesFilter).map(({ type, name }) => (
        <FixaListViewItem
          data-testid={`hfb-list-element-${type}`}
          title={name}
          key={type}
          control={multiSelect ? "checkbox" : "radiobutton"}
          inset
          onChange={() => {
            if (multiSelect) {
              handleCheckboxChange(type);
            } else {
              setArea(type);
              onSelect(type);
            }
          }}
          controlProps={{
            checked: multiSelect ? selectedAreas.includes(type) : type === area,
            value: type,
          }}
        />
      ))}
    </FixaListView>
  );
}
